import { Select } from "@bafe/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useRef, useState, type ReactNode } from "react";

import { api } from "../../lib/api.js";
import { queryKeys } from "../../lib/query.js";
import { HubManualLookupFieldSelect } from "./hub-manual-lookup-field-select.js";
import { compareLocale, defaultColumnVisibility } from "./hub-operation-list-table.js";
import {
	HubOperationBar,
	HubOperationColumnMenu,
	HubOperationFilterClear,
	HubOperationFilterMenu,
	HubOperationGroupHeader,
	HubOperationListTable,
	HubOperationTableEmptyRow,
	HubOperationTablePagination,
	hubOperationGroupedHasRows,
	HubOperationFormField,
	HubOperationFormRow,
	HubOperationFormStack,
	HubOperationModal,
	HubOperationScreen,
	HubOperationSearchField,
	HubOperationSearchToggle,
	HubOperationToolbar,
	hubTableSortIndicator,
	toggleHubSort,
	useHubOperationSearchFocus,
} from "./hub-operation-shell.js";
import { useHubOperationPagination } from "./hub-operation-pagination.js";
import {
	convertQtyToUnit,
	DEFAULT_CONSUMABLE_CATEGORIES,
	DEFAULT_CONSUMABLE_UNITS,
	formatQty,
	isLowStock,
	mergeUniqueLists,
	nextConsumableCode,
	parseQty,
	qtyAfterMovement,
	type ConsumableSettings,
} from "./hub-consumables-logic.js";
import type { ConsumableMovementRow, ConsumableRow, ConsumableSettingsRow, OrgSiteBooks } from "./hub-operation-types.js";
import {
	customerLabel,
	customersOf,
	formatMyr,
	INVENTORY_CUSTODY_TAGS,
	siteRow,
	sitesForCustomer,
	textOf,
	useHubSave,
	vendorLabel,
	vendorsOf,
} from "./hub-operation-shared.js";

export type ConsumablesBooks = OrgSiteBooks & {
	consumables: ConsumableRow[];
	consumableMovements: ConsumableMovementRow[];
	consumableSettings?: ConsumableSettingsRow[];
};

type ConsumableColumnId = "code" | "category" | "name" | "qty" | "minQty" | "location" | "unitPrice" | "brand";
const CONSUMABLE_TABLE_COLUMNS: Array<{ id: ConsumableColumnId; label: string; sortable?: boolean }> = [
	{ id: "code", label: "ID", sortable: true },
	{ id: "category", label: "Category", sortable: true },
	{ id: "name", label: "Item", sortable: true },
	{ id: "brand", label: "Brand", sortable: true },
	{ id: "qty", label: "Qty", sortable: true },
	{ id: "minQty", label: "Min", sortable: true },
	{ id: "location", label: "Custody", sortable: true },
	{ id: "unitPrice", label: "Unit price", sortable: true },
];

type ConsumableView = "items" | "history";
type ConsumableStockFilter = "" | "low" | "internal" | "external";
type ConsumableGroupBy = "category" | "";

function mergeOptionNames(lists: string[][]): string[] {
	const set = new Set<string>();
	for (const list of lists) {
		for (const name of list) {
			const trimmed = name.trim();
			if (trimmed) set.add(trimmed);
		}
	}
	return [...set].sort((a, b) => a.localeCompare(b));
}

function consumableSettingsRow(books: ConsumablesBooks): ConsumableSettingsRow | undefined {
	return books.consumableSettings?.[0];
}

async function saveConsumableSettings(books: ConsumablesBooks, patch: Partial<ConsumableSettingsRow>) {
	const existing = consumableSettingsRow(books);
	const record: ConsumableSettingsRow = {
		categories: existing?.categories ?? [],
		brands: existing?.brands ?? [],
		units: mergeUniqueLists(DEFAULT_CONSUMABLE_UNITS, existing?.units ?? []),
		conversions: existing?.conversions ?? [],
		...patch,
	};
	if (existing?.id != null) {
		await api.updateHubBook({ list: "consumableSettings", id: existing.id, record: { ...existing, ...record } });
		return;
	}
	await api.appendHubBook({ list: "consumableSettings", record });
}

async function ensureConsumableOption(
	books: ConsumablesBooks,
	field: "categories" | "brands",
	name: string,
) {
	const trimmed = name.trim();
	if (!trimmed) return;
	const existing = consumableSettingsRow(books);
	const custom = field === "categories" ? existing?.categories ?? [] : existing?.brands ?? [];
	if (custom.some((entry) => entry.toLowerCase() === trimmed.toLowerCase())) return;
	if (field === "categories" && DEFAULT_CONSUMABLE_CATEGORIES.some((entry) => entry.toLowerCase() === trimmed.toLowerCase())) {
		return;
	}
	const next = [...custom, trimmed].sort((a, b) => a.localeCompare(b));
	await saveConsumableSettings(books, field === "categories" ? { categories: next } : { brands: next });
}

async function removeConsumableOption(
	books: ConsumablesBooks,
	field: "categories" | "brands",
	name: string,
) {
	const trimmed = name.trim();
	if (!trimmed) return;
	const existing = consumableSettingsRow(books);
	if (!existing?.id) return;
	const custom = field === "categories" ? existing.categories ?? [] : existing.brands ?? [];
	const next = custom.filter((entry) => entry.trim().toLowerCase() !== trimmed.toLowerCase());
	await saveConsumableSettings(books, field === "categories" ? { categories: next } : { brands: next });
}

function settingsOf(books: ConsumablesBooks): ConsumableSettings {
	const row = consumableSettingsRow(books);
	return {
		categories: mergeUniqueLists(DEFAULT_CONSUMABLE_CATEGORIES, row?.categories ?? []),
		brands: mergeUniqueLists([], row?.brands ?? []),
		units: mergeUniqueLists(DEFAULT_CONSUMABLE_UNITS, row?.units ?? []),
		conversions: row?.conversions ?? [],
	};
}

function consumableCustodySummary(books: ConsumablesBooks, row: Pick<ConsumableRow, "location" | "customerId" | "siteId">): string {
	if (row.location === "external") {
		const customer = customerLabel(books, row.customerId ?? "");
		const site = siteRow(books, row.siteId ?? "")?.name ?? "";
		return [customer, site].filter(Boolean).join(" · ") || "With Client";
	}
	return "On Hands";
}

function consumableRecord(fields: FormData, code: string, existing?: ConsumableRow | null): ConsumableRow {
	const location = textOf(fields, "location") === "external" ? "external" : "internal";
	return {
		code,
		category: textOf(fields, "category"),
		name: textOf(fields, "name"),
		description: textOf(fields, "description"),
		brand: textOf(fields, "brand"),
		specification: textOf(fields, "specification"),
		unit: textOf(fields, "unit") || "pcs",
		qty: existing?.qty ?? (textOf(fields, "qty") || "0"),
		minQty: textOf(fields, "minQty") || "0",
		unitPrice: textOf(fields, "unitPrice") || "0",
		vendorId: textOf(fields, "vendorId"),
		location,
		customerId: location === "external" ? textOf(fields, "customerId") : "",
		siteId: location === "external" ? textOf(fields, "siteId") : "",
	};
}

function ConsumableFormFields({
	books,
	settings,
	editing,
	onRefresh,
}: {
	books: ConsumablesBooks;
	settings: ConsumableSettings;
	editing?: ConsumableRow | null;
	onRefresh: () => Promise<void>;
}) {
	const vendors = vendorsOf(books);
	const customers = customersOf(books);
	const consumableRows = books.consumables ?? [];
	const settingsRow = consumableSettingsRow(books);
	const categories = mergeOptionNames([
		settings.categories,
		consumableRows.map((row) => row.category),
	]);
	const brands = mergeOptionNames([
		settings.brands,
		consumableRows.map((row) => row.brand ?? ""),
	]);
	const removableCategories = settingsRow?.categories ?? [];
	const removableBrands = settingsRow?.brands ?? [];
	const [location, setLocation] = useState<ConsumableRow["location"]>(editing?.location ?? "internal");
	const [customerId, setCustomerId] = useState(editing?.customerId ?? "");
	const sites = sitesForCustomer(books, customerId);
	return (
		<HubOperationFormStack key={editing?.id ?? "new"}>
			{editing ? (
				<HubOperationFormField label="ID" htmlFor="con-code">
					<input id="con-code" name="code" value={editing.code} readOnly />
				</HubOperationFormField>
			) : (
				<input type="hidden" name="code" value={nextConsumableCode(books.consumables)} />
			)}
			<HubManualLookupFieldSelect
				id="con-category"
				name="category"
				label="Category"
				defaultValue={editing?.category ?? ""}
				items={categories}
				removableItems={removableCategories}
				usageLabel="consumable"
				countUsage={(name) => {
					const needle = name.trim().toLowerCase();
					return consumableRows.filter((row) => row.category.trim().toLowerCase() === needle).length;
				}}
				onRemove={async (name) => {
					await removeConsumableOption(books, "categories", name);
					await onRefresh();
				}}
			/>
			<HubOperationFormField label="Item name" htmlFor="con-name">
				<input id="con-name" name="name" defaultValue={editing?.name ?? ""} placeholder="Cat6 cable" required />
			</HubOperationFormField>
			<HubOperationFormField label="Description" htmlFor="con-description">
				<textarea id="con-description" name="description" rows={2} defaultValue={editing?.description ?? ""} placeholder="Optional" />
			</HubOperationFormField>
			<HubOperationFormRow>
				<HubManualLookupFieldSelect
					id="con-brand"
					name="brand"
					label="Brand"
					defaultValue={editing?.brand ?? ""}
					items={brands}
					removableItems={removableBrands}
					usageLabel="consumable"
					countUsage={(name) => {
						const needle = name.trim().toLowerCase();
						return consumableRows.filter((row) => (row.brand ?? "").trim().toLowerCase() === needle).length;
					}}
					onRemove={async (name) => {
						await removeConsumableOption(books, "brands", name);
						await onRefresh();
					}}
				/>
				<HubOperationFormField label="Specification" htmlFor="con-spec">
					<input id="con-spec" name="specification" defaultValue={editing?.specification ?? ""} />
				</HubOperationFormField>
			</HubOperationFormRow>
			<HubOperationFormRow>
				<HubOperationFormField label="Unit" htmlFor="con-unit">
					<Select id="con-unit" name="unit" defaultValue={editing?.unit ?? "pcs"} required>
						{settings.units.map((value) => <option key={value} value={value}>{value}</option>)}
					</Select>
				</HubOperationFormField>
				<HubOperationFormField label="Minimum stock" htmlFor="con-min">
					<input id="con-min" name="minQty" type="number" min="0" step="any" defaultValue={editing?.minQty ?? "0"} />
				</HubOperationFormField>
			</HubOperationFormRow>
			{editing ? (
				<HubOperationFormRow>
					<HubOperationFormField label="Quantity in stock" htmlFor="con-qty">
						<input id="con-qty" value={editing.qty || "0"} readOnly />
						<p className="quiet m-0">Use stock in, stock out, or transfer to change quantity.</p>
					</HubOperationFormField>
					<HubOperationFormField label="Unit purchase price" htmlFor="con-price">
						<input id="con-price" name="unitPrice" inputMode="decimal" defaultValue={editing.unitPrice ?? "0"} />
					</HubOperationFormField>
				</HubOperationFormRow>
			) : (
				<HubOperationFormRow>
					<HubOperationFormField label="Opening quantity" htmlFor="con-qty-new">
						<input id="con-qty-new" name="qty" type="number" min="0" step="any" defaultValue="0" />
					</HubOperationFormField>
					<HubOperationFormField label="Unit purchase price" htmlFor="con-price-new">
						<input id="con-price-new" name="unitPrice" inputMode="decimal" defaultValue="0" />
					</HubOperationFormField>
				</HubOperationFormRow>
			)}
			<HubOperationFormField label="Vendor" htmlFor="con-vendor">
				<Select id="con-vendor" name="vendorId" defaultValue={editing?.vendorId ?? ""}>
					<option value="">No vendor</option>
					{vendors.map((row) => (
						<option key={row.id ?? row.name} value={String(row.id ?? "")}>{row.name}</option>
					))}
				</Select>
			</HubOperationFormField>
			<HubOperationFormField label="Custody" htmlFor="con-location">
				<Select
					id="con-location"
					name="location"
					value={location}
					onChange={(event) => setLocation(event.target.value === "external" ? "external" : "internal")}
				>
					{INVENTORY_CUSTODY_TAGS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
				</Select>
			</HubOperationFormField>
			{location === "external" ? (
				<HubOperationFormRow>
					<HubOperationFormField label="Customer" htmlFor="con-customer">
						<Select
							id="con-customer"
							name="customerId"
							value={customerId}
							onChange={(event) => setCustomerId(event.target.value)}
							required
						>
							<option value="">Choose customer</option>
							{customers.map((row) => (
								<option key={row.id ?? row.name} value={String(row.id ?? "")}>{row.name}</option>
							))}
						</Select>
					</HubOperationFormField>
					<HubOperationFormField label="Site" htmlFor="con-site">
						<Select id="con-site" name="siteId" defaultValue={editing?.siteId ?? ""} required>
							<option value="">Choose site</option>
							{sites.map((row) => (
								<option key={row.id ?? row.name} value={String(row.id ?? "")}>{row.name}</option>
							))}
						</Select>
					</HubOperationFormField>
				</HubOperationFormRow>
			) : null}
		</HubOperationFormStack>
	);
}

function MovementFormFields({
	books,
	settings,
	target,
}: {
	books: ConsumablesBooks;
	settings: ConsumableSettings;
	target: ConsumableRow;
}) {
	const customers = customersOf(books);
	const [moveType, setMoveType] = useState<ConsumableMovementRow["type"]>("in");
	const [toLocation, setToLocation] = useState<ConsumableRow["location"]>(target.location);
	const [customerId, setCustomerId] = useState(target.customerId ?? "");
	const sites = sitesForCustomer(books, customerId);
	const displayQty = useMemo(() => {
		const unit = target.unit || "pcs";
		return `${formatQty(parseQty(target.qty))} ${unit}`;
	}, [target.qty, target.unit]);
	return (
		<HubOperationFormStack key={target.id ?? target.code}>
			<p className="quiet">{target.code} · {target.name} · On hand: {displayQty}</p>
			<input type="hidden" name="consumableId" value={String(target.id ?? "")} />
			<HubOperationFormField label="Movement" htmlFor="move-type">
				<Select id="move-type" name="type" value={moveType} onChange={(event) => setMoveType(event.target.value as ConsumableMovementRow["type"])}>
					<option value="in">Stock in</option>
					<option value="out">Stock out</option>
					<option value="transfer">Transfer</option>
				</Select>
			</HubOperationFormField>
			<HubOperationFormRow>
				<HubOperationFormField label="Quantity" htmlFor="move-qty">
					<input id="move-qty" name="qty" type="number" min="0" step="any" required />
				</HubOperationFormField>
				<HubOperationFormField label="Unit" htmlFor="move-unit">
					<Select id="move-unit" name="unit" defaultValue={target.unit || "pcs"}>
						{settings.units.map((value) => <option key={value} value={value}>{value}</option>)}
					</Select>
				</HubOperationFormField>
			</HubOperationFormRow>
			<HubOperationFormField label="Date" htmlFor="move-at">
				<input id="move-at" name="at" type="date" defaultValue={new Date().toISOString().slice(0, 10)} required />
			</HubOperationFormField>
			{moveType === "transfer" ? (
				<>
					<p className="quiet">From {consumableCustodySummary(books, target)}</p>
					<HubOperationFormField label="To custody" htmlFor="move-to">
						<Select
							id="move-to"
							name="toLocation"
							value={toLocation}
							onChange={(event) => setToLocation(event.target.value === "external" ? "external" : "internal")}
						>
							{INVENTORY_CUSTODY_TAGS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
						</Select>
					</HubOperationFormField>
					{toLocation === "external" ? (
						<>
							<HubOperationFormField label="Customer" htmlFor="move-customer">
								<Select id="move-customer" name="customerId" value={customerId} onChange={(event) => setCustomerId(event.target.value)} required>
									<option value="">Choose customer</option>
									{customers.map((row) => (
										<option key={row.id ?? row.name} value={String(row.id ?? "")}>{row.name}</option>
									))}
								</Select>
							</HubOperationFormField>
							<HubOperationFormField label="Site" htmlFor="move-site">
								<Select id="move-site" name="siteId" defaultValue={target.siteId ?? ""} required>
									<option value="">Choose site</option>
									{sites.map((row) => (
										<option key={row.id ?? row.name} value={String(row.id ?? "")}>{row.name}</option>
									))}
								</Select>
							</HubOperationFormField>
						</>
					) : null}
				</>
			) : null}
			<HubOperationFormField label="Note" htmlFor="move-note">
				<textarea id="move-note" name="note" rows={2} placeholder="Optional" />
			</HubOperationFormField>
		</HubOperationFormStack>
	);
}

function consumableCell(books: ConsumablesBooks, row: ConsumableRow, columnId: ConsumableColumnId): ReactNode {
	const low = isLowStock(row.qty, row.minQty);
	switch (columnId) {
		case "code":
			return row.code;
		case "category":
			return row.category || "—";
		case "name":
			return (
				<>
					{row.name}
					{low ? <span className="doc-pill warn">Low stock</span> : null}
				</>
			);
		case "brand":
			return row.brand || "—";
		case "qty":
			return `${formatQty(parseQty(row.qty))} ${row.unit}`;
		case "minQty":
			return formatQty(parseQty(row.minQty));
		case "location":
			return consumableCustodySummary(books, row);
		case "unitPrice":
			return formatMyr(row.unitPrice);
		default:
			return "—";
	}
}

export function ConsumablesHub({ books }: { books: ConsumablesBooks }) {
	const client = useQueryClient();
	const refreshBooks = async () => {
		await client.invalidateQueries({ queryKey: queryKeys.hubRecords });
	};
	const searchRef = useRef<HTMLInputElement>(null);
	const rows = books.consumables ?? [];
	const movements = books.consumableMovements ?? [];
	const settings = settingsOf(books);
	const [searchOpen, setSearchOpen] = useState(false);
	const [searchQuery, setSearchQuery] = useState("");
	const [view, setView] = useState<ConsumableView>("items");
	const [stockFilter, setStockFilter] = useState<ConsumableStockFilter>("");
	const [groupBy, setGroupBy] = useState<ConsumableGroupBy>("");
	const [collapsedGroups, setCollapsedGroups] = useState<string[]>([]);
	const [sortKey, setSortKey] = useState<ConsumableColumnId>("code");
	const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
	const [columnVisibility, setColumnVisibility] = useState(() => defaultColumnVisibility(CONSUMABLE_TABLE_COLUMNS));
	const [editing, setEditing] = useState<ConsumableRow | null>(null);
	const [moving, setMoving] = useState<ConsumableRow | null>(null);
	useHubOperationSearchFocus(searchOpen, searchRef);
	const needle = searchQuery.trim().toLowerCase();

	const searchMatched = useMemo(() => {
		if (!needle) return rows;
		return rows.filter((row) => {
			const site = row.siteId ? siteRow(books, row.siteId)?.name ?? "" : "";
			const customer = row.customerId ? customerLabel(books, row.customerId) : "";
			const vendor = row.vendorId ? vendorLabel(books, row.vendorId) : "";
			return [
				row.code,
				row.category,
				row.name,
				row.description,
				row.brand,
				row.specification,
				row.unit,
				customer,
				site,
				vendor,
			].some((value) => (value ?? "").toLowerCase().includes(needle));
		});
	}, [books, needle, rows]);

	const filtered = useMemo(() => {
		let list = searchMatched;
		if (stockFilter === "low") list = list.filter((row) => isLowStock(row.qty, row.minQty));
		if (stockFilter === "internal") list = list.filter((row) => row.location !== "external");
		if (stockFilter === "external") list = list.filter((row) => row.location === "external");
		return list;
	}, [searchMatched, stockFilter]);

	const sorted = useMemo(() => {
		const copy = [...filtered];
		copy.sort((a, b) => {
			let av = "";
			let bv = "";
			if (sortKey === "location") {
				av = consumableCustodySummary(books, a);
				bv = consumableCustodySummary(books, b);
			} else if (sortKey === "qty" || sortKey === "minQty") {
				av = String(parseQty(a[sortKey]));
				bv = String(parseQty(b[sortKey]));
			} else {
				av = String(a[sortKey] ?? "");
				bv = String(b[sortKey] ?? "");
			}
			return compareLocale(av, bv, sortDir);
		});
		return copy;
	}, [books, filtered, sortKey, sortDir]);

	const filteredMovements = useMemo(() => {
		const q = searchQuery.trim().toLowerCase();
		if (!q) return movements;
		return movements.filter((row) => {
			const item = rows.find((entry) => entry.id === row.consumableId);
			const type = row.type === "in" ? "Stock in" : row.type === "out" ? "Stock out" : "Transfer";
			return [row.at, row.note, row.qty, row.unit, type, item?.code, item?.name]
				.some((part) => String(part ?? "").toLowerCase().includes(q));
		});
	}, [movements, rows, searchQuery]);

	const itemsPaginationResetKey = `${searchQuery}|${stockFilter}|${groupBy}|${sortKey}|${sortDir}`;
	const historyPaginationResetKey = `${searchQuery}|history`;
	const itemsPagination = useHubOperationPagination(sorted.length, itemsPaginationResetKey);
	const historyPagination = useHubOperationPagination(filteredMovements.length, historyPaginationResetKey);
	const pagedSorted = useMemo(() => itemsPagination.sliceRows(sorted), [itemsPagination.sliceRows, sorted]);
	const pagedMovements = useMemo(
		() => historyPagination.sliceRows(filteredMovements),
		[filteredMovements, historyPagination.sliceRows],
	);
	const groupedRows = useMemo(() => {
		if (!groupBy) return [["", pagedSorted] as [string, ConsumableRow[]]];
		const map = new Map<string, ConsumableRow[]>();
		for (const row of pagedSorted) {
			const key = row.category.trim() || "General";
			const list = map.get(key) ?? [];
			list.push(row);
			map.set(key, list);
		}
		return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
	}, [pagedSorted, groupBy]);

	const form = useHubSave(async (fields) => {
		const category = textOf(fields, "category");
		const brand = textOf(fields, "brand");
		await Promise.all([
			category ? ensureConsumableOption(books, "categories", category) : Promise.resolve(),
			brand ? ensureConsumableOption(books, "brands", brand) : Promise.resolve(),
		]);
		const code = editing?.code ?? nextConsumableCode(rows);
		const record = consumableRecord(fields, code, editing);
		if (editing?.id) {
			await api.updateHubBook({ list: "consumables", id: editing.id, record: { ...editing, ...record, qty: editing.qty } });
		} else {
			const qty = formatQty(parseQty(textOf(fields, "qty") || "0"));
			await api.appendHubBook({ list: "consumables", record: { ...record, qty } });
		}
		setEditing(null);
	});

	const movementForm = useHubSave(async (fields) => {
		if (!moving?.id) return;
		const type = textOf(fields, "type") as ConsumableMovementRow["type"];
		const qty = textOf(fields, "qty");
		const unit = textOf(fields, "unit") || moving.unit;
		const at = textOf(fields, "at");
		const note = textOf(fields, "note");
		const nextQty = qtyAfterMovement(
			moving.qty,
			qty,
			unit,
			moving.unit,
			settings.conversions,
			moving.id,
			type,
		);
		const delta = convertQtyToUnit(parseQty(qty), unit, moving.unit, settings.conversions, moving.id);
		if (type === "out" && delta > parseQty(moving.qty)) {
			throw new Error("Not enough stock for that quantity.");
		}
		const toLocation = textOf(fields, "toLocation") === "external" ? "external" : "internal";
		const movement: ConsumableMovementRow = {
			consumableId: moving.id,
			type,
			qty,
			unit,
			at,
			note,
			fromLocation: moving.location,
			toLocation: type === "transfer" ? toLocation : undefined,
			customerId: type === "transfer" && toLocation === "external" ? textOf(fields, "customerId") : undefined,
			siteId: type === "transfer" && toLocation === "external" ? textOf(fields, "siteId") : undefined,
		};
		await api.appendHubBook({ list: "consumableMovements", record: movement });
		const patch: ConsumableRow = {
			...moving,
			qty: nextQty,
		};
		if (type === "transfer") {
			patch.location = toLocation;
			if (toLocation === "external") {
				patch.customerId = textOf(fields, "customerId");
				patch.siteId = textOf(fields, "siteId");
			} else {
				patch.customerId = "";
				patch.siteId = "";
			}
		}
		await api.updateHubBook({ list: "consumables", id: moving.id, record: patch });
		setMoving(null);
	});

	async function remove(row: ConsumableRow) {
		if (!row.id || !window.confirm(`Remove ${row.code}?`)) return;
		await api.deleteHubBook({ list: "consumables", id: row.id });
		await client.invalidateQueries({ queryKey: queryKeys.hubRecords });
	}

	const lowCount = rows.filter((row) => isLowStock(row.qty, row.minQty)).length;
	const searchActive = searchQuery.trim().length > 0;
	const listFiltered = Boolean(stockFilter);
	const filteredLabel = searchActive || listFiltered || view === "history";
	const visibleColumns = CONSUMABLE_TABLE_COLUMNS.filter((column) => columnVisibility[column.id]);
	const colSpan = visibleColumns.length + 1;

	const lowStockCount = searchMatched.filter((row) => isLowStock(row.qty, row.minQty)).length;
	const internalCount = searchMatched.filter((row) => row.location !== "external").length;
	const externalCount = searchMatched.filter((row) => row.location === "external").length;

	function toggleSort(key: ConsumableColumnId) {
		const next = toggleHubSort(sortKey, sortDir, key);
		setSortKey(next.sortKey as ConsumableColumnId);
		setSortDir(next.sortDir);
	}

	function clearFilters() {
		setStockFilter("");
	}

	function renderConsumableRow(row: ConsumableRow) {
		return (
			<tr key={row.id ?? row.code}>
				{visibleColumns.map((column) => (
					<td key={column.id} data-label={column.label}>
						{consumableCell(books, row, column.id)}
					</td>
				))}
				<td className="tight" onClick={(event) => event.stopPropagation()}>
					{row.id ? (
						<details className="select menu">
							<summary aria-label="Actions"><svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="12" cy="5" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="12" cy="19" r="1.8" /></svg></summary>
							<div className="select-pop">
								<button className="select-item" type="button" onClick={() => { setEditing(row); form.setOpen(true); }}>Edit</button>
								<button className="select-item" type="button" onClick={() => { setMoving(row); movementForm.setOpen(true); }}>Stock movement</button>
								<button className="select-item" type="button" onClick={() => void remove(row)}>Delete</button>
							</div>
						</details>
					) : null}
				</td>
			</tr>
		);
	}

	return (
		<HubOperationScreen>
			<HubOperationBar sub={`${view === "history" ? filteredMovements.length : sorted.length} ${view === "history" ? (filteredMovements.length === 1 ? "movement" : "movements") : (sorted.length === 1 ? "consumable" : "consumables")}${lowCount && view === "items" ? ` · ${lowCount} low` : ""}${filteredLabel ? " · filtered" : ""}`}>
				<HubOperationToolbar
					searchSlot={(
						<HubOperationSearchField
							id="hub-consumables-search"
							ariaLabel={view === "history" ? "Search stock history" : "Search consumables"}
							placeholder={view === "history" ? "Search history…" : "Search consumables…"}
							searchOpen={searchOpen}
							searchQuery={searchQuery}
							searchRef={searchRef}
							onQueryChange={setSearchQuery}
							onEscape={() => { setSearchQuery(""); setSearchOpen(false); }}
						/>
					)}
					tools={(
						<>
							<HubOperationSearchToggle
								searchOpen={searchOpen}
								searchActive={searchActive}
								ariaLabel={view === "history" ? "Search stock history" : "Search consumables"}
								onToggle={() => {
									if (searchOpen) setSearchQuery("");
									setSearchOpen(!searchOpen);
								}}
							/>
							{view === "items" ? (
								<HubOperationColumnMenu
									columns={CONSUMABLE_TABLE_COLUMNS}
									visibility={columnVisibility}
									onToggle={(id, checked) => setColumnVisibility((current) => ({ ...current, [id]: checked }))}
								/>
							) : null}
						</>
					)}
					primaryAction={(
						<button className="btn" type="button" onClick={() => { setEditing(null); form.setOpen(true); }}>New consumable</button>
					)}
				/>
			</HubOperationBar>
			<div className="task-filters">
				<HubOperationFilterMenu
					label="View"
					ariaLabel="Consumables view"
					value={view}
					onPick={(next) => setView(next === "history" ? "history" : "items")}
					active={view === "history"}
					items={[
						["items", "Items", rows.length],
						["history", "History", movements.length],
					]}
				/>
				{view === "items" ? (
					<HubOperationFilterMenu
						label="Stock"
						ariaLabel="Stock filter"
						value={stockFilter}
						onPick={(next) => setStockFilter(next as ConsumableStockFilter)}
						active={Boolean(stockFilter)}
						counted
						items={[
							["", "All", searchMatched.length],
							["low", "Low stock", lowStockCount],
							["internal", "On Hands", internalCount],
							["external", "With Client", externalCount],
						]}
					/>
				) : null}
				{view === "items" ? (
					<HubOperationFilterMenu
						label="Group"
						ariaLabel="Group list"
						value={groupBy}
						onPick={(next) => {
							setGroupBy(next as ConsumableGroupBy);
							setCollapsedGroups([]);
						}}
						active={false}
						items={[
							["", "All"],
							["category", "Category"],
						]}
					/>
				) : null}
				{view === "items" && listFiltered ? <HubOperationFilterClear onClear={clearFilters} /> : null}
			</div>
			{view === "history" ? (
				<HubOperationListTable>
					<div className="table-wrap hub-inventory-table-wrap">
						<table className="hub-inventory-table">
							<thead>
								<tr>
									<th>Date</th>
									<th>Item</th>
									<th>Type</th>
									<th>Qty</th>
									<th>Note</th>
								</tr>
							</thead>
							<tbody>
								{filteredMovements.length === 0 ? (
									<HubOperationTableEmptyRow colSpan={5}>
										{movements.length === 0 ? "No stock movements yet." : "No movements match your search."}
									</HubOperationTableEmptyRow>
								) : pagedMovements.map((row) => {
									const item = rows.find((entry) => entry.id === row.consumableId);
									return (
										<tr key={row.id ?? `${row.consumableId}-${row.at}-${row.type}`}>
											<td data-label="Date">{row.at}</td>
											<td data-label="Item">{item ? `${item.code} · ${item.name}` : `#${row.consumableId}`}</td>
											<td data-label="Type">{row.type === "in" ? "Stock in" : row.type === "out" ? "Stock out" : "Transfer"}</td>
											<td data-label="Qty">{row.qty} {row.unit}</td>
											<td data-label="Note">{row.note || "—"}</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
					<HubOperationTablePagination
						total={filteredMovements.length}
						page={historyPagination.page}
						pageSize={historyPagination.pageSize}
						totalPages={historyPagination.totalPages}
						onPage={historyPagination.setPage}
						onPageSize={historyPagination.setPageSize}
						totalNoun="movements"
					/>
				</HubOperationListTable>
			) : (
				<HubOperationListTable>
				<div className="table-wrap hub-inventory-table-wrap">
					<table className="hub-inventory-table">
						<thead>
							<tr>
								{visibleColumns.map((column) => (
									<th key={column.id}>
										{column.sortable ? (
											<button type="button" className="th-sort plain" onClick={() => toggleSort(column.id)}>
												{column.label}
												{hubTableSortIndicator(sortKey, column.id, sortDir)}
											</button>
										) : column.label}
									</th>
								))}
								<th className="tight"></th>
							</tr>
						</thead>
						<tbody>
							{!hubOperationGroupedHasRows(groupedRows) ? (
								<HubOperationTableEmptyRow colSpan={colSpan}>
									{filteredLabel || rows.length > 0 ? "No consumables match your filters." : "No consumables yet."}
								</HubOperationTableEmptyRow>
							) : groupedRows.flatMap(([groupLabel, groupItems]) => {
								const collapsed = collapsedGroups.includes(groupLabel);
								const header = groupBy ? (
									<HubOperationGroupHeader
										key={`grp-${groupLabel}`}
										label={groupLabel || "General"}
										collapsed={collapsed}
										colSpan={colSpan}
										onToggle={() => setCollapsedGroups((current) => (
											current.includes(groupLabel) ? current.filter((c) => c !== groupLabel) : [...current, groupLabel]
										))}
									/>
								) : null;
								return [
									...(header ? [header] : []),
									...(groupBy && collapsed ? [] : groupItems.map((row) => renderConsumableRow(row))),
								];
							})}
						</tbody>
					</table>
				</div>
				<HubOperationTablePagination
					total={sorted.length}
					page={itemsPagination.page}
					pageSize={itemsPagination.pageSize}
					totalPages={itemsPagination.totalPages}
					onPage={itemsPagination.setPage}
					onPageSize={itemsPagination.setPageSize}
					totalNoun="consumables"
				/>
				</HubOperationListTable>
			)}
			<HubOperationModal
				hubForm
				open={form.open}
				title={editing ? "Edit consumable" : "New consumable"}
				error={form.error}
				pending={form.pending}
				onClose={() => { form.setOpen(false); setEditing(null); }}
				onSubmit={(fields) => void form.save(fields)}
			>
				<ConsumableFormFields books={books} settings={settings} editing={editing} onRefresh={refreshBooks} />
			</HubOperationModal>
			<HubOperationModal
				hubForm
				open={movementForm.open}
				title="Stock movement"
				error={movementForm.error}
				pending={movementForm.pending}
				action="Record"
				onClose={() => { movementForm.setOpen(false); setMoving(null); }}
				onSubmit={(fields) => void movementForm.save(fields)}
			>
				{moving ? <MovementFormFields books={books} settings={settings} target={moving} /> : null}
			</HubOperationModal>
		</HubOperationScreen>
	);
}
